// Mirrors src/lib/radio-schedule.ts (which carries the unit tests). Keep in sync.
// Reusable across any RADIO venue, no React, no I/O, unit-tested in isolation.
// Times are station-local ("HH:MM", 24h); the station's IANA timezone anchors
// them to real instants so listeners see the correct show wherever they are.

export type DayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday … 6 = Saturday (JS Date.getDay)

export type ScheduleEntry = {
  day: DayIndex;
  /** Station-local start, "HH:MM" 24h. */
  start: string;
  /** Station-local end, "HH:MM". If <= start, the show runs past midnight. */
  end: string;
  show: string;
  host?: string;
  genres?: string[];
};

export type NowPlaying = {
  /** The show on air at `now`, if any. */
  current?: ScheduleEntry;
  /** The next show to start after `now`. */
  next?: ScheduleEntry;
  /** Minutes until `next` starts, for countdowns. Undefined if no next. */
  startsInMinutes?: number;
};

const DAY = 24 * 60;
const WEEK = 7 * DAY; // 10080

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":");
  return (parseInt(h, 10) % 24) * 60 + (parseInt(m ?? "0", 10) || 0);
}

/** Absolute week-minute of an entry's start (0 = Sun 00:00 … 10079). */
function startWeekMinute(e: ScheduleEntry): number {
  return e.day * DAY + toMinutes(e.start);
}

/** Duration in minutes, treating end <= start as a past-midnight wrap. */
function durationMinutes(e: ScheduleEntry): number {
  const d = toMinutes(e.end) - toMinutes(e.start);
  return d > 0 ? d : d + DAY;
}

/** Station-local {day, minutes-since-midnight} for an instant + IANA timezone. */
export function stationLocal(now: Date, timeZone: string): { day: DayIndex; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const days: Record<string, DayIndex> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const day = days[get("weekday")] ?? 0;
  let hour = parseInt(get("hour"), 10);
  if (hour === 24 || Number.isNaN(hour)) hour = 0; // some engines emit "24" at midnight
  const minutes = hour * 60 + (parseInt(get("minute"), 10) || 0);
  return { day, minutes };
}

/**
 * Resolve which show is on air at `now` and which is next, on a weekly cycle.
 * Works entirely in week-minutes on a circle, so overnight and week-boundary
 * wraps are handled without date arithmetic.
 */
export function resolveNowPlaying(
  entries: ScheduleEntry[],
  now: Date,
  timeZone = "Europe/London",
): NowPlaying {
  if (!entries.length) return {};
  const { day, minutes } = stationLocal(now, timeZone);
  const nowWM = day * DAY + minutes;

  let current: ScheduleEntry | undefined;
  let next: ScheduleEntry | undefined;
  let nextDelta = Infinity;

  for (const e of entries) {
    const start = startWeekMinute(e);
    const sinceStart = (((nowWM - start) % WEEK) + WEEK) % WEEK; // forward distance start→now
    if (sinceStart < durationMinutes(e)) current = e; // now inside [start, start+duration)

    const untilStart = (((start - nowWM) % WEEK) + WEEK) % WEEK; // forward distance now→start
    if (untilStart > 0 && untilStart < nextDelta) {
      nextDelta = untilStart;
      next = e;
    }
  }

  return { current, next, startsInMinutes: next ? nextDelta : undefined };
}

/** Entries for one station-local day, ordered by start time. */
export function entriesForDay(entries: ScheduleEntry[], day: DayIndex): ScheduleEntry[] {
  return entries
    .filter((e) => e.day === day)
    .sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
}
